import {Injectable} from "@angular/core";
import {ParameterRule} from "@ispm-client/modules/query-builder/models/query-builder-config";
import {FactorBaseService} from "./factor-base-service";

@Injectable()
export class BiomarkerService extends FactorBaseService{
  static readonly ORIGIN = "origin";
  static readonly SOMATIC = "Somatic";
  static readonly GENE = "gene";
  static readonly MARKER = "marker";
  static readonly VALUE = "value";
  static readonly UNITS = "units";
  static readonly DETECTIONMETHOD = "detectionmethod";
  static readonly COPYNUMBER = "copynumber";
  static readonly CHROMOSOMENUMBER = "chromosomenumber";
  static readonly CHROMOSOMEARM = "chromosomearm";
  static readonly CYTOBANDSTART = "cytobandstart";
  static readonly CYTOBANDEND = "cytobandend";
  static readonly ABNORMALITY = "Abnormality";
  static readonly DOMAIN = "domain";
  static readonly LOCATION = "location";
  static readonly EXON = "exon";
  static readonly CONSEQUENCE = "consequence";
  static readonly MUTATION = "Mutation";
  static readonly ALTERATION = "alteration";
  static readonly ABERRATION = "Aberration";
  static readonly WILD_TYPE = "Wild Type";
  static readonly CHR = "chr";
  static readonly P = "p";
  static readonly Q = "q";
  static readonly HYPHEN = "-";
  static readonly BY = "by";
  static readonly TESTTYPE = "testtype";
  static readonly TESTSCORE = "testscore";
  static readonly TESTUSEFULNESS = "testusefulness";
  static readonly MMRSTATUS = "mmrstatus";
  static readonly MSSTATUS = "msstatus";
  static readonly TMB = "tmb";
  static readonly TMBVALUE = "tmbvalue";
  static readonly MMR_UNKNOWN = "MMR Unknown";
  static readonly UNKNOWN = "unknown";
  static readonly PARTNERGENE = "partnergene";
  static readonly CNV = "CNV";
  static readonly METHYLATION = "Methylation";
  static readonly REARRANGEMENT = "rearrangement";
  static readonly INDEL = "INDEL";


  public getDisplayName(parameterRule: ParameterRule) : string {
    //create a dictionary of Rules where the attribute type is the key (e.g., gene, origin)
    this.parameterRulesMap = {};
    let parameterRules = parameterRule.rules;
    for (let rule of parameterRules) {
      this.parameterRulesMap[rule.field.toLowerCase()] = rule;
    }

    //get biomarker type
    let parameter: string = parameterRule.parameter.toLowerCase();
    let biomarkerName: string = "";
    switch (parameter.toLowerCase()) {
      case "biomarkerexpression": {
        biomarkerName = this.biomarkerExpression();
        break;
      }
      case "biomarkerother": {
        biomarkerName = this.biomarkerOther();
        break;
      }
      case "biomarkermethylation": {
        biomarkerName = this.biomarkerMethylation();
        break;
      }
      case "biomarkercnv": {
        biomarkerName = this.biomarkerCnv();
        break;
      }
      case "biomarkeraberration": {
        biomarkerName = this.biomarkerAberration();
        break;
      }
      case "biomarkerchromosomalabnormality": {
        biomarkerName = this.biomarkerChromosomalAbnormality();
        break;
      }
      case "biomarkerrearrangement": {
        biomarkerName = this.biomarkerRearrangement();
        break;
      }
      case "biomarkerwildtype": {
        biomarkerName = this.biomarkerWildtype();
        break;
      }
      case "biomarkermutation": {
        biomarkerName = this.biomarkerMutation();
        break;
      }
    }
    return biomarkerName;
  }

  private biomarkerExpression(): string {
    // Biomarker - Expression:
    // NOVALUE
    // {Origin!=Somatic} {Gene}          {Type}                 [by {Method}]
    // {Origin!=Somatic} {Gene}                 {Value} {Units} [by {Method}]
    // {Origin!=Somatic}        {Marker} {Type}                 [by {Method}]
    // {Origin!=Somatic}        {Marker}        {Value} {Units} [by {Method}]
    // {Origin!=Somatic} {Gene} {Marker} {Type} {Value} {Units} [by {Method}]
    //
    // Example: Origin=Somatic, Gene=EGFR, Type=Mutation => EGFR Mutation

    var results: string[] = new Array();

    //use template that has all possibilities; check existence in correct order
    // {Origin!=Somatic} {Gene} {Marker} {Type} {Value} {Units} [by {Method}]

    this.originNotSomatic(results);
    this.gene(results);
    this.marker(results);
    this.type(results);
    this.value(results);
    this.units(results);
    this.detectionMethod(results);

    let delimitedString = results.join(BiomarkerService.SPACE);
    return delimitedString;
  }

  private biomarkerCnv(): string {
    // Biomarker - CNV:
    // {Origin!=Somatic} {Gene} {Type} {Number} [by {Method}]

    var results: string[] = new Array();
    let rule = null;

    //use template that has all possibilities
    this.originNotSomatic(results);
    this.gene(results);
    this.cnvType(results);
    this.copyNumber(results);
    this.detectionMethod(results);

    let delimitedString = results.join(BiomarkerService.SPACE);
    return delimitedString;
  }

  private biomarkerChromosomalAbnormality(): string {
    // Biomarker - Chromosomal Abnormality:
    // {Origin!=Somatic} chr{Chromosome Number}{Chromosome Arm} {Cytoband Start}[-{Cytoband End}] {Type|Abnormality}
    //
    // eg:                  {Chromosome Number=1}{Chromosome Arm=p} chr1p
    // eg: {Origin=Somatic }{Chromosome Number=1}{Chromosome Arm=p} chr1p
    // eg: {Origin=Germline}{Chromosome Number=1}{Chromosome Arm=p} Germline chr1p
    //
    // eg: {Chromosome Number=1}{Chromosome Arm=p}{Cytoband Start=36.33} chr1p36.33
    // eg: {Chromosome Number=1}{Chromosome Arm=p}{Cytoband Start=36.33}{Cytoband End=38.20} chr1p36.33-38.20
    //
    // eg: {Chromosome Number=1}               chr1 Abnormality
    // eg: {Chromosome Number=1}{Type=Trisomy} chr1 Trisomy

    var results: string[] = new Array();
    let rule = null;

    this.originNotSomatic(results);
    this.chromosomeNumber(results);
    this.chromosomeArm(results);
    this.cytobandStart(results);
    this.cytobandEnd(results);
    this.chromosomalType(results);

    let delimitedString = results.join(BiomarkerService.SPACE);
    delimitedString = this.removeUnwantedSpaces(delimitedString);
    return delimitedString;
  }

  private biomarkerMethylation(): string {
    // Biomarker - Methylation:
    // {Origin!=Somatic} {Gene} {Type}

    var results: string[] = new Array();
    let rule = null;

    this.originNotSomatic(results);
    this.gene(results);
    this.methylationType(results);

    let delimitedString = results.join(BiomarkerService.SPACE);
    return delimitedString;
  }

  private biomarkerMutation(): string {
    // Biomarker - Mutation:
    // {Origin!=Somatic} {Gene} {Domain} {Location}                     {Consequence} {Type|Mutation} [by {Method}]
    // {Origin!=Somatic} {Gene}          {Location} {Exon}              {Consequence} {Type|Mutation} [by {Method}]
    // {Origin!=Somatic} {Gene}          {Location}        {Alteration}                               [by {Method}]
    // {Origin!=Somatic} {Gene} {Domain} {Location} {Exon} {Alteration} {Consequence} {Type|Mutation} [by {Method}] *

    var results: string[] = new Array();
    let rule = null;

    //choose template - use template that has all possibilities; check existence in correct order
    this.originNotSomatic(results);
    this.gene(results);
    this.domain(results);
    this.location(results);
    this.exon(results);
    this.alteration(results);
    this.consequence(results);
    this.mutationType(results);
    this.detectionMethod(results);

    let delimitedString = results.join(BiomarkerService.SPACE);
    return delimitedString;
  }

  private biomarkerOther(): string {
    // Biomarker - Other:
    // {Origin!=Somatic} {Test Type} {Score} {Usefulness}
    //
    // TMB {TMB Status}
    // TMB              {TMB Value}
    // TMB {TMB Status} {TMB Value} *
    //
    // {MMR Status!=Unknown|MMR Unknown}
    //
    // {MS Status!=Unknown}
    // MS Status {MS Status==Unknown}

    /*
      if (TMB status || TMB value) push 'TMB',
      push TMB Status
      push TMB Value

      if (MMR Status == Unknown) push 'MMR Unknown' else push MMR Status

      if (MS Status == Unknown) push 'MS STatus Unknown' else push MS Status

      The last one is simple
      the code above may also push empty elements
      before you join all elements with spaces, remove the empty elements
     */

    var results: string[] = new Array();

    //template: {Origin!=Somatic} {Test Type} {Score} {Usefulness}
    this.originNotSomatic(results);
    this.testType(results);
    this.testScore(results);
    this.testUsefulness(results);

    // if (TMB status || TMB value) push 'TMB',
    // push TMB Status
    // push TMB Value
    if (this.keyExists(BiomarkerService.TMB) || this.keyExists(BiomarkerService.TMBVALUE)) {
      results.push(BiomarkerService.TMB.toUpperCase());
      this.tmb(results);
      this.tmbValue(results);
    }

    // if (MMR Status == Unknown) push 'MMR Unknown' else push MMR Status
    // if (MS Status == Unknown) push 'MS STatus Unknown' else push MS Status
    this.mmrStatus(results);
    this.msStatus(results);

    let delimitedString = results.join(BiomarkerService.SPACE);
    return delimitedString;
  }

  private biomarkerRearrangement(): string {
    // Biomarker - Rearrangement:
    // {Origin!=Somatic} {Gene}[-{Partner Gene}] {Value}

    var results: string[] = new Array();

    this.originNotSomatic(results);
    this.gene(results);
    this.partnerGene(results);
    this.valueDisplay(results);

    let delimitedString = results.join(BiomarkerService.SPACE);
    delimitedString = this.removeSpacesAroundMinusSign(delimitedString);
    return delimitedString;
  }

  private biomarkerAberration(): string {
    // Biomarker - Aberration:
    //{Origin!=Somatic} {Gene} Aberration [by {Detection Method}]

    var results: string[] = new Array();

    this.originNotSomatic(results);
    this.gene(results);
    results.push(BiomarkerService.ABERRATION);
    this.detectionMethod(results);

    let delimitedString = results.join(BiomarkerService.SPACE);
    delimitedString = this.removeSpacesAroundMinusSign(delimitedString);
    return delimitedString;
  }

  private biomarkerWildtype(): string {
    // Biomarker - Wildtype:
    //{Origin!=Somatic} {Gene} Wild Type [by {Detection Method}]

    var results: string[] = new Array();

    this.originNotSomatic(results);
    this.gene(results);
    results.push(BiomarkerService.WILD_TYPE);
    this.detectionMethod(results);

    let delimitedString = results.join(BiomarkerService.SPACE);
    return delimitedString;
  }

  private originNotSomatic(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService.ORIGIN];
    if (rule) {
      if (rule.value.display !== BiomarkerService.SOMATIC) {
        let displayValue: string = rule.value.display;
        this.pushWithOperator(rule, displayValue, results);
      }
    }
  }

  private gene(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService.GENE];
    if (rule) {
      let displayValue: string = rule.value.CommonName;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private partnerGene(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService.PARTNERGENE];
    if (rule) {
      let displayValue: string = rule.value.CommonName;
      results.push(BiomarkerService.HYPHEN);
      this.pushWithOperator(rule, displayValue, results);
    }
    else {
      results.push(BiomarkerService.REARRANGEMENT);
    }
  }

  private detectionMethod(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService.DETECTIONMETHOD];
    if (rule) {
      let displayValue: string = rule.value.display;
      results.push(BiomarkerService.BY);
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private units(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService.UNITS];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private type(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService._TYPE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private cnvType(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService._TYPE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
    else {
      results.push(BiomarkerService.CNV);
    }
  }

  private methylationType(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService._TYPE];
    if (rule) {
      let displayValue: string = rule.value.display;
      if (displayValue.toLowerCase() === BiomarkerService.UNKNOWN) {
        this.pushWithOperator(rule, BiomarkerService.METHYLATION, results);
        results.push(displayValue);
      }
      else {
        this.pushWithOperator(rule, displayValue, results);
      }
    }
    else {
      results.push(BiomarkerService.METHYLATION);
    }
  }

  private value(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService.VALUE];
    if (rule) {
      let displayValue: string = rule.value;
      this.pushWithOperator(rule, displayValue, results);
    }
  }
  
  private valueDisplay(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService.VALUE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private marker(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService.MARKER];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private copyNumber(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService.COPYNUMBER];
    if (rule) {
      let displayValue: string = rule.value;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private cytobandEnd(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService.CYTOBANDEND];
    if (rule) {
      let displayValue: string = rule.value;
      results.push("-");
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private cytobandStart(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService.CYTOBANDSTART];
    if (rule) {
      let displayValue: string = rule.value;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private chromosomeArm(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService.CHROMOSOMEARM];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private chromosomeNumber(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService.CHROMOSOMENUMBER];
    if (rule) {
      let displayValue: string = BiomarkerService.CHR + rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private domain(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService.DOMAIN];
    if (rule) {
      let displayValue: string = rule.value;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private location(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService.LOCATION];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private exon(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService.EXON];
    if (rule) {
      let displayValue: string = rule.value;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private alteration(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService.ALTERATION];
    if (rule) {
      let displayValue: string = rule.value;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private consequence(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService.CONSEQUENCE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private tmbValue(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService.TMBVALUE];
    if (rule) {
      let displayValue: string = rule.value;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private tmb(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService.TMB];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private testType(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService.TESTTYPE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private testScore(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService.TESTSCORE];
    if (rule) {
      let displayValue: string = rule.value;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private testUsefulness(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService.TESTUSEFULNESS];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }
  
  private mutationType(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService._TYPE];
    if (rule) {
      let displayValue: string = rule.value.display;
      if (displayValue.startsWith(BiomarkerService.INDEL)) {
        displayValue = BiomarkerService.INDEL;
      }
      else {
        displayValue = displayValue.substring(0,3);
      }
      this.pushWithOperator(rule, displayValue, results);
    } else {
      results.push(BiomarkerService.MUTATION);
    }
  }
  
  private chromosomalType(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService._TYPE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
    else {
      results.push(BiomarkerService.ABNORMALITY);
    }
  }

  private msStatus(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService.MSSTATUS];
    if (rule) {
      let displayValue: string = rule.value.display;
      if (displayValue === BiomarkerService.UNKNOWN) {
        displayValue = BiomarkerService.MMR_UNKNOWN
      }
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private mmrStatus(results: string[]) {
    let rule = this.parameterRulesMap[BiomarkerService.MMRSTATUS];
    if (rule) {
      let displayValue: string = rule.value.display;
      if (displayValue === BiomarkerService.UNKNOWN) {
        displayValue = BiomarkerService.MMR_UNKNOWN
      }
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private removeUnwantedSpaces(delimitedString) {
    delimitedString = this.removeUnwantedChromsomeArmSpace(delimitedString);
    delimitedString = this.removeSpacesAroundMinusSign(delimitedString);
    return delimitedString;
  }

  private removeUnwantedChromsomeArmSpace(delimitedString) {
    delimitedString = delimitedString.replace(BiomarkerService.SPACE + BiomarkerService.P + BiomarkerService.SPACE, BiomarkerService.P + BiomarkerService.SPACE);
    delimitedString = delimitedString.replace(BiomarkerService.SPACE + BiomarkerService.Q + BiomarkerService.SPACE, BiomarkerService.Q + BiomarkerService.SPACE);
    return delimitedString;
  }

  private removeSpacesAroundMinusSign(delimitedString: string): string {
    return delimitedString.replace(BiomarkerService.SPACE + BiomarkerService.HYPHEN + BiomarkerService.SPACE, BiomarkerService.HYPHEN);
  }

}
